import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
/**
 * Maximum length OPREF accepts for a saved-filter name.
 *
 * Deliberately NOT bound to the input's `maxlength` attribute (OP-7357): a native
 * `maxlength="100"` silently stops accepting keystrokes/paste content past the limit, so a
 * user typing a longer name never sees any feedback and the field just saves the truncated,
 * exactly-100-character value. The AC requires an inline error that blocks the save instead —
 * that only works if the field can actually HOLD the over-long value long enough for
 * {@link useFilterNameField}'s `validate()` to reject it.
 */
export const FILTER_NAME_MAX_LENGTH = 100;
/**
 * The name field shared by the save and rename filter modals.
 *
 * Both need the same draft ref, the same reset-on-reopen, the same trim → required → too-long
 * ladder and the same "local error wins over the parent's backend error" precedence. Keeping
 * one implementation means the validation contract exists once, and the per-flow difference
 * (which i18n keys, whether a name is seeded) is an explicit parameter rather than a
 * copy that drifts — the duplicate had already started to, one watcher carrying
 * `{ immediate: true }` and the other not, and the rename modal borrowing the save modal's
 * "too long" key across namespaces.
 */
export function useFilterNameField(options) {
    const { t } = useI18n();
    const name = ref(options.initialName?.() ?? "");
    const localError = ref(null);
    // Reset on every (re)open so neither a previous attempt's error nor the previously
    // targeted filter's name leaks into the next one. Not `immediate`: the ref above is
    // already seeded, and a modal is always mounted closed.
    watch(() => options.isOpen(), (open) => {
        if (!open)
            return;
        name.value = options.initialName?.() ?? "";
        localError.value = null;
    });
    const shownError = computed(() => localError.value ?? options.errorMessage());
    function validate() {
        const trimmed = name.value.trim();
        if (!trimmed) {
            localError.value = t(options.requiredKey);
            return null;
        }
        if (trimmed.length > FILTER_NAME_MAX_LENGTH) {
            localError.value = t(options.tooLongKey);
            return null;
        }
        localError.value = null;
        return trimmed;
    }
    return { name, shownError, validate };
}
